import { Link } from 'react-router-dom';
import { Car, Tag, TrendingUp, Globe, Gamepad2, MountainSnow, Cpu } from 'lucide-react';
import SiteLink from './SiteLink';

const SITES = [
  { id: 'auto', label: 'CarsbikesPrices', desc: 'Vehicle Prices & Reviews', icon: Car, color: 'bg-red-500', to: '/auto', links: [
    { label: 'Cars', to: '/auto/listings?type=car' },
    { label: 'Bikes', to: '/auto/listings?type=bike' },
    { label: 'Compare', to: '/auto/compare' },
    { label: 'News', to: '/auto/news' },
  ]},
  { id: 'coupon', label: 'MetroMandi', desc: 'Deals, Coupons & Savings', icon: Tag, color: 'bg-purple-600', to: '/coupons', links: [
    { label: 'All Coupons', to: '/coupons' },
    { label: 'Food & Dining', to: '/coupons/browse/food' },
    { label: 'Fashion', to: '/coupons/browse/fashion' },
    { label: 'Free Recharge', to: '/free-recharge' },
  ]},
  { id: 'finance', label: 'YourMoneyWise', desc: 'Calculators, Compare & Learn', icon: TrendingUp, color: 'bg-emerald-600', to: '/finance', links: [
    { label: 'SIP Calculator', to: '/finance/calculators/sip' },
    { label: 'Compare Products', to: '/finance/compare' },
    { label: 'Finance Jobs', to: '/finance/jobs' },
    { label: 'Courses', to: '/finance/learn' },
  ]},
  { id: 'games', label: 'GamesNFans', desc: 'Esports, Gaming & Cricket', icon: Gamepad2, color: 'bg-yellow-500', to: '/games', links: [
    { label: 'Cricket', to: '/games/cricket' },
    { label: 'Esports', to: '/games/esports' },
    { label: 'Gaming', to: '/games/gaming' },
    { label: 'Soccer', to: '/games/soccer' },
  ]},
  { id: 'travel', label: 'WokeTraveler', desc: 'Conscious Travel & Trip Tips', icon: MountainSnow, color: 'bg-emerald-500', to: '/travel/blog', links: [
    { label: 'Itineraries', to: '/travel/blog?cat=Itineraries' },
    { label: 'Gear Reviews', to: '/travel/blog?cat=Gear' },
    { label: 'Destinations', to: '/travel/blog?cat=Destinations' },
    { label: 'Digital Nomad', to: '/travel/blog?cat=Digital%20Nomad' },
  ]},
  // Tech filters by ?topic=<key> (pages/tech/techTopics.js), not ?cat=.
  { id: 'tech', label: 'Tech-N-AI Talks', desc: 'AI Tools, Software & Gadgets', icon: Cpu, color: 'bg-indigo-600', to: '/tech', links: [
    { label: 'AI Tools', to: '/tech/blog?topic=ai' },
    { label: 'VPN & Privacy', to: '/tech/blog?topic=privacy' },
    { label: 'Software', to: '/tech/blog?topic=software' },
    { label: 'Gadgets', to: '/tech/blog?topic=gadgets' },
  ]},
];

const SITE_DISPLAY_NAMES = {
  auto: 'CarsbikesPrices', coupon: 'MetroMandi', finance: 'YourMoneyWise',
  games: 'GamesNFans', travel: 'WokeTraveler', tech: 'Tech-N-AI Talks',
};

export default function NetworkFooter({ activeSite = 'all', bgClass = 'bg-gray-950', disclaimer = '', containerClass = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' }) {
  const siteName = SITE_DISPLAY_NAMES[activeSite] || 'this site';
  return (
    <footer className={`${bgClass} text-white py-12`} data-testid="network-footer">
      <div className={containerClass}>

        {/* Network Sites Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          {SITES.map(site => (
            <div key={site.id}>
              <div className="flex items-center gap-2 mb-3">
                {/* each site's own mark, the same asset as its favicon and app icon */}
                <img src={`/favicon-${site.id}-192.png`} alt="" width="28" height="28" loading="lazy"
                  className="w-7 h-7 rounded-md object-contain bg-white/5" />
                <SiteLink to={site.to} site={site.id} className="font-semibold text-sm hover:text-gray-300 transition-colors">
                  {site.label}
                </SiteLink>
              </div>
              <p className="text-xs text-gray-300 mb-2">{site.desc}</p>
              <ul className="space-y-1.5">
                {site.links.map(link => (
                  <li key={link.to}>
                    <SiteLink to={link.to} site={site.id} className="text-xs text-gray-200 hover:text-white transition-colors">
                      {link.label}
                    </SiteLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Legal Links */}
        <div className="border-t border-gray-700 pt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-200 mb-6">
          <Link to="/about" className="hover:text-white">About Us</Link>
          <Link to="/contact" className="hover:text-white">Contact Us</Link>
          <Link to="/terms" className="hover:text-white">Terms of Use</Link>
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/disclaimer" className="hover:text-white">Disclaimer</Link>
          <Link to="/sitemap" className="hover:text-white" data-testid="footer-sitemap">Sitemap</Link>
        </div>

        {/* Disclaimer + Copyright */}
        <div className="space-y-2">
          {disclaimer && (
            <p className="text-[11px] text-gray-300 leading-relaxed max-w-3xl">
              {disclaimer} See our <Link to="/disclaimer" className="underline hover:text-white">full disclaimer</Link>.
            </p>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-gray-300">
            <p>&copy; 2026 {siteName}. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
